/**
 * Utility functions for product and variant operations
 */

/**
 * Formats a price value for display
 * @param {string|number} price - The price to format
 * @returns {string} Formatted price string
 */
export const formatPriceForDisplay = (price) => {
  if (price === null || price === undefined || price === '') {
    return '';
  }
  
  // Convert to number and format with 2 decimal places
  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    return '';
  }
  
  return numPrice.toFixed(2);
};

/**
 * Formats a price value for API submission
 * @param {string|number} price - The price to format
 * @returns {string} Formatted price string for API
 */
export const formatPriceForApi = (price) => {
  if (price === null || price === undefined || price === '') {
    return '';
  }
  
  // Remove any non-numeric characters except decimal point
  let cleanPrice = price.toString().replace(/[^0-9.]/g, '');
  
  // Convert to number and format as string
  const numPrice = parseFloat(cleanPrice);
  if (isNaN(numPrice)) {
    return '';
  }
  
  return numPrice.toString();
};

/**
 * Get the value of a variant field, checking for local edits first
 * @param {Object|string} variantOrProductId - Either the variant object or the product ID
 * @param {Object|string} variantUpdatesOrVariantId - Either the variant updates object or the variant ID
 * @param {string} field - The field name to get
 * @param {*} [defaultValue] - Default value if using the new format
 * @param {Object} [localEdits] - Local edits object if using the new format
 * @param {Array} [products] - Optional array of products to get original variant data
 * @returns {*} The field value
 */
export const getVariantFieldValue = (variantOrProductId, variantUpdatesOrVariantId, field, defaultValue, localEdits, products = []) => {
  // Check if we're using the new format (5+ parameters)
  if (typeof variantOrProductId === 'string' && typeof variantUpdatesOrVariantId === 'string' && localEdits) {
    const productId = variantOrProductId;
    const variantId = variantUpdatesOrVariantId.toString(); // Ensure variant ID is a string
    
    // Check if we have local edits for this field
    if (localEdits[productId]?.variant_updates?.[variantId]?.[field] !== undefined) {
      const value = localEdits[productId].variant_updates[variantId][field];
      return value;
    }
    
    // Also check for the variant ID as a number (for backward compatibility)
    const numericVariantId = parseInt(variantId, 10);
    if (!isNaN(numericVariantId) && 
        localEdits[productId]?.variant_updates?.[numericVariantId]?.[field] !== undefined) {
      const value = localEdits[productId].variant_updates[numericVariantId][field];
      return value;
    }
    
    // If products array is provided, try to find the original variant data
    if (products && products.length > 0) {
      const product = products.find(p => p.id === productId);
      if (product && product.variants) {
        const originalVariant = product.variants.find(v => v.id === variantId || v.id === numericVariantId);
        if (originalVariant && originalVariant[field] !== undefined) {
          return originalVariant[field];
        }
        
        // Try looking in originalVariants if available
        if (product.originalVariants) {
          const originalVariant = product.originalVariants.find(v => v.id === variantId || v.id === numericVariantId);
          if (originalVariant && originalVariant[field] !== undefined) {
            return originalVariant[field];
          }
        }
      }
    }
    
    // Otherwise return the default value
    return defaultValue;
  } 
  // Using the legacy format (3 parameters)
  else {
    const variant = variantOrProductId;
    const variantUpdates = variantUpdatesOrVariantId;
    
    // Check if we have local edits for this field
    if (variantUpdates && variantUpdates[field] !== undefined) {
      const value = variantUpdates[field];
      return value;
    }
    
    // Otherwise return the original value
    const value = variant[field];
    return value;
  }
};

/**
 * Set the value of a variant field in the local edits
 * @param {Object} localEdits - Local edits object
 * @param {string} productId - The product ID
 * @param {string} variantId - The variant ID
 * @param {string} field - The field name to set
 * @param {*} value - The value to set
 * @param {Array} [products] - Optional array of products to get original variant data
 * @returns {Object} The updated local edits
 */
export const setVariantFieldValue = (localEdits, productId, variantId, field, value, products = []) => {
  // Ensure variant ID is a string
  const variantIdStr = variantId.toString();
  
  // Create a deep copy of the local edits
  const newLocalEdits = { ...localEdits };
  
  // Ensure we have a product entry
  if (!newLocalEdits[productId]) {
    newLocalEdits[productId] = {};
  }
  
  // Ensure we have a variant_updates entry
  if (!newLocalEdits[productId].variant_updates) {
    newLocalEdits[productId].variant_updates = {};
  }
  
  // Find the original variant data from the products array
  let originalVariantData = {};
  
  if (products && products.length > 0) {
    const product = products.find(p => p.id === productId);
    if (product && product.variants) {
      const originalVariant = product.variants.find(v => v.id === variantId || v.id === variantIdStr);
      if (originalVariant) {
        // Store all original variant data including image
        originalVariantData = { ...originalVariant };
        console.log(`Found original variant data for ${variantIdStr}:`, originalVariantData);
      } else if (product.originalVariants) {
        // Try looking in originalVariants if available
        const originalVariant = product.originalVariants.find(v => v.id === variantId || v.id === variantIdStr);
        if (originalVariant) {
          originalVariantData = { ...originalVariant };
          console.log(`Found original variant data from originalVariants for ${variantIdStr}:`, originalVariantData);
        }
      }
    }
  }
  
  // If we don't have an entry for this variant yet, initialize it with complete data
  if (!newLocalEdits[productId].variant_updates[variantIdStr]) {
    // Get existing variant data from window.localEdits if available
    let existingVariantData = {};
    if (typeof window !== 'undefined' && window.localEdits && 
        window.localEdits[productId] && window.localEdits[productId].variants && 
        window.localEdits[productId].variants[variantIdStr]) {
      existingVariantData = { ...window.localEdits[productId].variants[variantIdStr] };
      console.log(`Found existing variant data in window.localEdits for ${variantIdStr}:`, existingVariantData);
    }
    
    // Initialize with original data or empty object with defaults
    newLocalEdits[productId].variant_updates[variantIdStr] = {
      // Store the variant ID
      id: variantIdStr,
      
      // Store all the original variant data
      ...originalVariantData,
      
      // Ensure critical fields have default values if not in original data
      // Prioritize existing values from window.localEdits, then original data, then defaults
      price: existingVariantData.price || originalVariantData.price || "0.00",
      compareAtPrice: existingVariantData.compareAtPrice || 
                      originalVariantData.compareAtPrice || 
                      originalVariantData.compare_at_price || "",
      sku: existingVariantData.sku || originalVariantData.sku || "",
      inventoryQuantity: existingVariantData.inventoryQuantity || 
                         originalVariantData.inventoryQuantity || 
                         originalVariantData.inventory_quantity || 
                         (originalVariantData.inventory && originalVariantData.inventory.quantity) || 0,
      
      // Store option values in a consistent format
      options: existingVariantData.options || 
               originalVariantData.options || 
               (originalVariantData.option1 ? [
                 originalVariantData.option1,
                 originalVariantData.option2,
                 originalVariantData.option3
               ].filter(Boolean) : [])
    };
    
    console.log(`Initialized variant ${variantIdStr} in local edits with complete data:`, 
               newLocalEdits[productId].variant_updates[variantIdStr]);
  }
  
  // Set the field value
  newLocalEdits[productId].variant_updates[variantIdStr][field] = value;
  
  // Also update options array if option1, option2, or option3 is being set
  if (field === 'option1' || field === 'option2' || field === 'option3') {
    const optionIndex = parseInt(field.replace('option', ''), 10) - 1;
    if (!newLocalEdits[productId].variant_updates[variantIdStr].options) {
      newLocalEdits[productId].variant_updates[variantIdStr].options = [];
    }
    newLocalEdits[productId].variant_updates[variantIdStr].options[optionIndex] = value;
  }
  
  return newLocalEdits;
};

/**
 * Directly update the variant SKU input fields in the UI
 * @param {string} productId - The product ID
 * @param {Array|Object} variants - Array of variant objects with id and sku properties, or object with variant IDs as keys
 */
export const updateVariantSkuFieldsInUI = (productId, variants) => {
  if (!variants) {
    return;
  }

  // Convert variants to array format if it's an object
  const variantsArray = Array.isArray(variants) 
    ? variants 
    : Object.entries(variants).map(([id, data]) => ({ id, ...data }));

  if (variantsArray.length === 0) {
    return;
  }

  // Process each variant
  variantsArray.forEach(variant => {
    if (!variant || !variant.id || variant.sku === undefined) {
      return;
    }

    const variantId = variant.id.toString();
    const skuValue = variant.sku;

    // Extract the numeric ID from the Shopify variant ID
    const numericId = variantId.replace('gid://shopify/ProductVariant/', '');
    
    // Try multiple selector patterns to find the SKU input field
    const selectors = [
      // Standard selectors from before
      `input[data-variant-id="${variantId}"][data-field="sku"]`,
      `input[data-variant-id="${numericId}"][data-field="sku"]`,
      `input#variant-${numericId}-sku`,
      `input[name*="${numericId}"][name*="sku"]`,
      
      // New Shopify Admin selectors
      `input[name="variants[${numericId}][sku]"]`,
      `input[id="Variants[${numericId}].sku"]`,
      `input[id="Variant-${numericId}-sku"]`,
      `input[id*="${numericId}"][id*="sku"]`,
      
      // More generic selectors as fallbacks
      `[data-variant-id="${numericId}"] input[name*="sku"]`,
      `[data-variant-index="${numericId}"] input[name*="sku"]`,
      `[data-variant-index="${numericId}"] input[id*="sku"]`,
      `[data-variant-position="${numericId}"] input[name*="sku"]`,
      `tr[data-row-key*="${numericId}"] input[name*="sku"]`,
      `tr[data-row-key*="${numericId}"] input[id*="sku"]`,
      
      // Last resort - any input with both variant ID and SKU in attributes
      `input[id*="${numericId}"][id*="sku"], input[name*="${numericId}"][name*="sku"]`
    ];

    // Try each selector
    let foundInputs = [];
    for (const selector of selectors) {
      try {
        const inputs = document.querySelectorAll(selector);
        if (inputs.length > 0) {
          foundInputs = inputs;
          break;
        }
      } catch (error) {
        // Silently continue to the next selector
      }
    }

    if (foundInputs.length > 0) {
      // Update each matching input field
      foundInputs.forEach(input => {
        try {
          // Store the original value for React state tracking
          const originalValue = input.value;
          
          // Update the input value
          input.value = skuValue;
          
          // Dispatch input and change events to ensure the UI updates
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          
          // For React-based inputs, try to update the internal state
          if (input._valueTracker) {
            input._valueTracker.setValue(originalValue);
          }
        } catch (error) {
          // Silently continue
        }
      });
    } else {
      // Try a more aggressive approach - find all inputs and look for ones with SKU in their name or ID
      const allInputs = document.querySelectorAll('input');
      const potentialSkuInputs = Array.from(allInputs).filter(input => {
        const id = input.id || '';
        const name = input.name || '';
        return (id.includes('sku') || name.includes('sku')) && 
               (id.includes(numericId) || name.includes(numericId));
      });
      
      if (potentialSkuInputs.length > 0) {
        potentialSkuInputs.forEach(input => {
          try {
            // Store the original value for React state tracking
            const originalValue = input.value;
            
            // Update the input value
            input.value = skuValue;
            
            // Dispatch input and change events to ensure the UI updates
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
            
            // For React-based inputs, try to update the internal state
            if (input._valueTracker) {
              input._valueTracker.setValue(originalValue);
            }
          } catch (error) {
            // Silently continue
          }
        });
      }
    }
  });
};

/**
 * Debug function to inspect the DOM structure for variant SKU fields
 * @param {string} productId - The product ID
 */
export const debugVariantSkuFields = (productId) => {
  // Get all input elements on the page
  const allInputs = document.querySelectorAll('input');
  
  // Look for potential SKU fields
  const potentialSkuFields = Array.from(allInputs).filter(input => {
    const id = input.id || '';
    const name = input.name || '';
    const className = input.className || '';
    const dataField = input.getAttribute('data-field') || '';
    const dataVariantId = input.getAttribute('data-variant-id') || '';
    
    return (
      id.includes('sku') || 
      name.includes('sku') || 
      className.includes('sku') || 
      dataField === 'sku' ||
      (dataVariantId && (id.includes('sku') || name.includes('sku')))
    );
  });
  
  // Look for elements with variant in their attributes
  const variantElements = document.querySelectorAll('[id*="variant"], [data-variant-id], [name*="variant"]');
};
